import React, { useState, useCallback, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme, alpha } from '@mui/material/styles';
import logoAtlas from '../../../assets/logo_atlas.png';
import { getPortalBranding } from '../portalBranding';
import { PortalBrand, portalBrandStyles, portalHeaderStyles, getPortalLogoStyles } from '../PortalBrand';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BarChartIcon from '@mui/icons-material/BarChart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useMsal } from '@azure/msal-react';
import { useImpersonationStore } from '../../../stores/useImpersonationStore';
import { useImpersonatableUsers } from '../../../hooks/queries/useImpersonation';
import { UserSelectionModal } from '../../impersonation/UserSelectionModal';
import { getImpersonationUserLabel } from '../../../services/impersonation.service';
import { evictPermissionsCache } from '../../../services/permissions.service';

export const navItems = [
  { path: '/', labelKey: 'nav.home', icon: DashboardIcon },
  { path: '/consumptions', labelKey: 'nav.consumptions', icon: BarChartIcon },
  { path: '/invoices', labelKey: 'nav.invoices', icon: ReceiptIcon },
  { path: '/contracts', labelKey: 'nav.contracts', icon: AssignmentIcon },
  { path: '/tasks', labelKey: 'nav.gestiones', icon: SupportAgentIcon },
  { path: '/help', labelKey: 'nav.ayuda', icon: HelpOutlineIcon },
  { path: '/news', labelKey: 'nav.news', icon: NewspaperOutlinedIcon },
];

export const isNavigationRouteActive = (pathname, path) =>
  pathname === path || (path !== '/' && pathname.startsWith(path + '/'));

const TopNavigation = ({ userName }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const { instance, accounts } = useMsal();

  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);

  // Impersonation state
  const { impersonatedUser, isSuperuser, setImpersonatedUser } = useImpersonationStore();
  const { data: impersonatableUsers } = useImpersonatableUsers(isSuperuser && !!impersonatedUser);

  useEffect(() => {
    if (!isSuperuser || !impersonatedUser || !Array.isArray(impersonatableUsers)) return;

    const currentUser = impersonatableUsers.find(
      user => user.email.toLowerCase() === impersonatedUser.email.toLowerCase(),
    );
    if (currentUser && currentUser.displayName !== impersonatedUser.displayName) {
      setImpersonatedUser({ ...impersonatedUser, displayName: currentUser.displayName });
    }
  }, [impersonatableUsers, impersonatedUser, isSuperuser, setImpersonatedUser]);

  const account = accounts[0];
  const claims = account?.idTokenClaims;
  const userEmail = account?.username || claims?.preferred_username || claims?.email ||
    claims?.emails?.[0] || claims?.['signInNames.emailAddress'];
  const actualUserDisplayName = account?.name?.trim() || userName || userEmail || 'User';
  const showEmail = userEmail && actualUserDisplayName.toLowerCase() !== userEmail.toLowerCase();
  const { clientName } = getPortalBranding(theme, t('header.portalTitle'));
  const brandLogo = theme.clientName ? (theme.logo || logoAtlas) : logoAtlas;

  // User menu should always show the real signed-in superuser, not the impersonated user.
  const displayName = actualUserDisplayName;

  const handleUserMenuOpen = (event) => setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);

  const handleLogout = useCallback(async () => {
    try {
      await evictPermissionsCache();
    } catch (e) {
      console.error('Permission cache eviction failed on logout', e);
    }
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }, [instance]);

  const toggleMobileDrawer = () => setMobileDrawerOpen(!mobileDrawerOpen);

  const isActiveRoute = (path) => isNavigationRouteActive(location.pathname, path);

  const ui = theme.palette.atlas?.ui;
  const navColors = {
    background: theme.palette.background.paper,
    secondaryBackground: theme.palette.action.hover,
    text: theme.palette.text.primary,
    textInactive: theme.palette.text.primary,
    activeIndicator: ui ? theme.palette.atlas.navigation.activeIndicator : theme.palette.primary.main,
  };
  const styles = {
    primaryBar: portalHeaderStyles,
    secondaryBar: {
      backgroundColor: navColors.background, minHeight: '44px !important',
      px: { sm: 2, md: 4.5 }, borderBottom: 1, borderColor: 'divider',
      overflowX: 'auto',
    },
    logo: getPortalLogoStyles(theme),
    ghostButton: {
      color: 'text.primary', backgroundColor: 'transparent', border: 0,
      borderRadius: 1, padding: '4px 8px', textTransform: 'none',
      display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
      textAlign: 'left', minWidth: 0,
      '&:hover': { backgroundColor: 'action.hover' },
      '&:focus-visible': { outline: '2px solid', outlineColor: ui?.focus ?? 'primary.main', outlineOffset: 2 },
    },
    navLink: {
      color: 'text.primary', textDecoration: 'none', whiteSpace: 'nowrap',
      padding: '9px 14px 8px', fontSize: '0.875rem', fontWeight: 400,
      display: 'flex', alignItems: 'center', borderBottom: '4px solid transparent',
      '&:hover': { backgroundColor: 'action.hover', color: ui?.selectedForeground ?? 'primary.main' },
      '&:focus-visible': { outline: '2px solid', outlineColor: ui?.focus ?? 'primary.main', outlineOffset: -2 },
    },
    navLinkActive: {
      color: ui?.selectedForeground ?? 'primary.main', backgroundColor: ui ? 'transparent' : undefined, fontWeight: 700,
      borderBottomColor: navColors.activeIndicator,
    },
    mobileDrawer: { width: 280 },
  };

  // Mobile Drawer Content
  const mobileDrawerContent = (
    <Box sx={styles.mobileDrawer}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Box component="img" src={brandLogo} alt={clientName} sx={styles.logo} />
        <IconButton onClick={toggleMobileDrawer} aria-label={t('header.closeNavigation')}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <List>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = isActiveRoute(item.path);
          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                component={NavLink}
                to={item.path}
                onClick={toggleMobileDrawer}
                selected={isActive}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: ui ? 'transparent' : alpha(theme.palette.primary.main, 0.1),
                    borderLeft: `4px solid ${navColors.activeIndicator}`,
                    ...(ui && {
                      '&:hover': { backgroundColor: 'action.hover' },
                      '&.Mui-focusVisible': { backgroundColor: 'action.focus' },
                    }),
                  },
                }}
              >
                <ListItemIcon>
                  <Icon color={isActive ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <ListItemText primary={t(item.labelKey)} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="fixed" elevation={0} sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        {/* Client branding and account controls */}
        <Toolbar sx={styles.primaryBar}>
          {/* Mobile hamburger */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={toggleMobileDrawer}
            aria-label={t('header.openNavigation')}
            aria-expanded={mobileDrawerOpen}
            sx={{ display: { sm: 'none' }, color: navColors.text }}
          >
            <MenuIcon />
          </IconButton>

          <Box component={NavLink} to="/" sx={portalBrandStyles}>
            <PortalBrand />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Impersonation indicator and switch button */}
          {isSuperuser && impersonatedUser && (
            <Chip
              label={t('impersonation.viewingAs', { user: getImpersonationUserLabel(impersonatedUser) })}
              size="small"
              sx={{
                mr: 2,
                backgroundColor: navColors.secondaryBackground,
                color: navColors.text,
                fontWeight: 500,
                display: { xs: 'none', md: 'flex' },
              }}
            />
          )}

          {isSuperuser && (
            <Tooltip title={t('impersonation.changeUser')}>
              <IconButton
                onClick={() => setShowUserModal(true)}
                sx={{
                  mr: 1,
                  color: navColors.text,
                  backgroundColor: alpha('#fff', 0.1),
                  '&:hover': {
                    backgroundColor: alpha('#fff', 0.2),
                  },
                }}
              >
                <SwapHorizIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* User Menu - Ghost Button Style */}
          <Box
            component="button"
            onClick={handleUserMenuOpen}
            aria-label={t('header.userMenu')}
            aria-haspopup="menu"
            aria-expanded={Boolean(userMenuAnchor)}
            sx={styles.ghostButton}
          >
            <PersonIcon sx={{ fontSize: 18 }} />
            <Box component="span" sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', minWidth: 0, maxWidth: 240 }}>
              <Typography component="span" variant="body2" fontWeight={600} noWrap>{displayName}</Typography>
              {showEmail && <Typography component="span" variant="caption" color="text.secondary" noWrap>{userEmail}</Typography>}
            </Box>
            <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
          </Box>
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            disableScrollLock
          >
            <MenuItem disabled>
              <Typography variant="body2">
                {t('header.hello')}, {displayName}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem component={NavLink} to="/profile" onClick={handleUserMenuClose}>
              <ListItemIcon>
                <VerifiedUserIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t('header.showProfile')} />
            </MenuItem>
            {isSuperuser && (
              <MenuItem onClick={() => { handleUserMenuClose(); setShowUserModal(true); }}>
                <ListItemIcon>
                  <SwapHorizIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('impersonation.changeUser')} />
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <ExitToAppIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t('header.closeSession')} />
            </MenuItem>
          </Menu>
        </Toolbar>

        {/* Shared portal navigation */}
        <Toolbar variant="dense" sx={{ ...styles.secondaryBar, display: { xs: 'none', sm: 'flex' } }}>
          <Box component="nav" aria-label={t('header.navigation')} sx={{ display: 'flex', alignItems: 'center' }}>
            {navItems.map((item) => {
              const isActive = isActiveRoute(item.path);
              return (
                <Box
                  key={item.path}
                  component={NavLink}
                  to={item.path}
                  sx={{
                    ...styles.navLink,
                    ...(isActive && styles.navLinkActive),
                  }}
                >
                  {t(item.labelKey)}
                </Box>
              );
            })}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        sx={{ zIndex: theme.zIndex.drawer + 2 }}
        anchor="left"
        open={mobileDrawerOpen}
        onClose={toggleMobileDrawer}
        ModalProps={{ keepMounted: true }}
      >
        {mobileDrawerContent}
      </Drawer>

      {/* User Selection Modal for Superusers */}
      <UserSelectionModal
        open={showUserModal}
        onClose={() => setShowUserModal(false)}
      />
    </>
  );
};

export default TopNavigation;
